'use client';

import { useId } from 'react';
import {lookupIcon} from 'heroicons-lookup';

import UiUtils from '@utils/ui';

export default function SpinnerButton(props) {
    const { 
        children, 
        size, 
        variant, 
        label, 
        leftIcon,
        leftIconSVG, 
        leftIconFill,
        rightIcon, 
        rightIconSVG, 
        rightIconFill,
        iconType, 
        className,
        ariaLabel, 
        disabled,
        spinning, 
        spinningSRText,
        onClick, 
        ...otherProps 
    } = props;
    let buttonClass = "relative flex justify-center items-center gap-1.5 font-vg-medium border rounded-lg leading-120";
    switch (size) {
        case 'small':
            buttonClass = `h-10 px-4 ${buttonClass}`;
            break;
        case 'large':
            buttonClass = `h-[52px] px-6 ${buttonClass}`;
            break;
        case 'x-small':
            buttonClass = `h-8 px-2 ${buttonClass}`;
            break;
        default:
            break;
    }
    let spinnerClass = "animate-spin inline-block absolute inset-0 m-auto size-6 border-3 border-current border-t-transparent rounded-full";
    let useVariant = disabled ? "disabled" : variant; 
    switch (useVariant) {
        case 'solid':
            buttonClass = `${buttonClass} text-base text-white bg-blue border-blue hover:bg-blue02 hover:border-blue02 active:bg-blue01 active:border-blue01 cursor-pointer`;
            spinnerClass = `${spinnerClass} text-white`;
            break;
        case 'outline':
            buttonClass = `${buttonClass} text-base bg-white text-blue border-blue hover:text-blue02 hover:border-blue02 active:text-blue01 active:border-blue01 cursor-pointer`;
            spinnerClass = `${spinnerClass} text-blue`;
            break;
        case 'ghost':
            buttonClass = `${buttonClass} text-base bg-transparent border-white text-blue hover:text-blue02 active:text-blue01 cursor-pointer`;
            spinnerClass = `${spinnerClass} text-blue`;
            break;
        case 'indicator-ghost':
            buttonClass = `${buttonClass} text-sm bg-transparent border-white text-blue hover:text-blue02 active:text-blue01 cursor-pointer`;
            spinnerClass = `${spinnerClass} text-blue`;
            break;
        case 'navlink':
            buttonClass = `${buttonClass} text-base bg-white border-white text-text-grey hover:text-grey active:text-grey cursor-pointer`;
            spinnerClass = `${spinnerClass} text-blue`;
            break;
        case 'delete':
            buttonClass = `${buttonClass} text-base bg-white text-red border-red hover:text-red hover:border-red active:text-red active:border-red cursor-pointer`;
            spinnerClass = `${spinnerClass} text-red`;
            break;
        case 'disabled':
            buttonClass = `${buttonClass} text-base bg-white text-grey border-grey cursor-not-allowed`;
            spinnerClass = `${spinnerClass} text-grey`;
            break;
        default:
            break;
    }
    if (className) {
        buttonClass = `${buttonClass} ${className}`;
    }

    let IconLeft = null;
    let IconRight = null;

    const iconLibrary = iconType ? iconType : "outline";

    if (leftIcon) {
        IconLeft = lookupIcon(leftIcon, iconLibrary);
    }

    if (rightIcon) {
        IconRight = lookupIcon(rightIcon, iconLibrary);
    }

    let ariaLabelId = useId();

    const getIconLeft = () => {
        const cls = `h-5${spinning ? " invisible" : ""}`;
        return (
            <>
                {IconLeft && (<IconLeft className={cls}/>)}
                {leftIconSVG && leftIconFill && (
                    UiUtils.getSVGIcon(leftIconSVG, leftIconFill, cls)
                )}
            </>
        );
    }

    const getIconRight = () => {
        const cls = `h-5${spinning ? " invisible" : ""}`;
        return (
            <>
                {IconRight && (<IconRight className={cls}/>)}
                {rightIconSVG && leftIconFill && (
                    UiUtils.getSVGIcon(rightIconSVG, rightIconFill, {cls})
                )}
            </>
        );
    }

    return (
        <>
            {ariaLabel ? (
                <button
                    className={buttonClass}
                    onClick={(e) => {
                        if (onClick && !spinning) {
                            onClick(e);
                        }
                    }}
                    disabled={useVariant === "disabled"}
                    aria-labelledby={ariaLabelId}
                    { ...otherProps } 
                >
                    {getIconLeft()}
                    <span className={spinning ? " invisible" : ""}>{label}</span>
                    {getIconRight()}
                    <span id={ariaLabelId} className="sr-only">{ariaLabel}</span>
                    {spinning && (
                        <div className={spinnerClass} role="status" aria-label={spinningSRText ? spinningSRText : "loading"}>
                           <span class="sr-only">{spinningSRText ? spinningSRText : "Loading..."}</span>
                        </div>
                    )}
                </button>
            ) : (
                <button
                    className={buttonClass}
                    onClick={(e) => {
                        if (onClick && !spinning) {
                            onClick(e);
                        }
                    }}
                    disabled={useVariant === "disabled"}
                    { ...otherProps } 
                >
                    {getIconLeft()}
                    <span className={spinning ? " invisible" : ""}>{label}</span>
                    {getIconRight()}
                    {spinning && (
                        <div className={spinnerClass} role="status" aria-label={spinningSRText ? spinningSRText : "loading"}>
                           <span class="sr-only">{spinningSRText ? spinningSRText : "Loading..."}</span>
                        </div>
                    )}
                </button>
            )}
        </>
    );
}